import { computed, ref } from 'vue';
import type { Component, Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useTableFilterModal } from '@/shared/lib/use';
import type { RecordSearchOutcome } from './useRecordSearch';

interface ListSortState {
  sort: string;
  order: string;
}

export interface ListControlsOptions {
  refetch: () => Promise<RecordSearchOutcome>;
  onCommitted: () => void;
}

interface ListControlsConfig<TSort extends ListSortState, TFilters extends object, TParams> {
  filterModal: Component;
  parseSort: (query: Record<string, unknown>) => TSort;
  parseFilters: (query: Record<string, unknown>) => TFilters;
  toSort: (sort: unknown, order: unknown) => TSort;
  pickFilters: (filters: TFilters) => TFilters;
  countFilters: (filters: TFilters) => number;
  buildUrlQuery: (sort: TSort, filters: TFilters) => Record<string, string | number>;
  toRequestParams: (sort: TSort, filters: TFilters) => TParams;
}

export function useListControls<TSort extends ListSortState, TFilters extends object, TParams>(
  config: ListControlsConfig<TSort, TFilters, TParams>,
  { refetch, onCommitted }: ListControlsOptions
) {
  const route = useRoute();

  const sortState = ref(config.parseSort(route.query)) as Ref<TSort>;
  const filters = ref(config.parseFilters(route.query)) as Ref<TFilters>;

  const sort = computed(() => sortState.value.sort);
  const order = computed(() => sortState.value.order);

  const filterCount = computed(() => config.countFilters(filters.value));
  const isFilterActive = computed(() => filterCount.value > 0);

  const requestParams = computed(() => config.toRequestParams(sortState.value, filters.value));
  const urlQuery = computed(() => config.buildUrlQuery(sortState.value, filters.value));

  const commit = async (mutate: () => void) => {
    const previous = { sortState: sortState.value, filters: filters.value };
    mutate();

    const outcome = await refetch();

    if (outcome === 'failed') {
      sortState.value = previous.sortState;
      filters.value = previous.filters;
      return;
    }

    if (outcome === 'ok') onCommitted();
  };

  const handleSortUpdate = (nextSort: string, nextOrder: string) =>
    commit(() => {
      sortState.value = config.toSort(nextSort, nextOrder);
    });

  const handleFilterUpdate = (next: TFilters) =>
    commit(() => {
      filters.value = config.pickFilters(next);
    });

  const { openFilterModal } = useTableFilterModal<TFilters>(
    config.filterModal,
    { clearFilters: () => undefined, filters: filters.value },
    (next) => void handleFilterUpdate(next),
    () => ({ ...filters.value }),
    () => ({
      onClearFilters: () => void handleFilterUpdate({} as TFilters),
    })
  );

  const reset = () => {
    sortState.value = config.toSort('', '');
    filters.value = {} as TFilters;
  };

  return {
    sort,
    order,
    filters,
    filterCount,
    isFilterActive,
    requestParams,
    urlQuery,
    handleSortUpdate,
    handleFilterUpdate,
    openFilterModal,
    reset,
  };
}
